import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Select, Button, Row, Col, Typography, Space, Modal } from "antd";
import { toast } from "../../components/toasts/toastStore";
import SuccessOverlay from "../../components/feedback/SuccessOverlay";
import { crearUsuario, getRoles, crearRol } from "./usuariosApi";

import "./EventosPage.css";
import "./PaquetesPage.css";

const { Title, Text } = Typography;

export default function CrearUsuarioPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);

  const [crearRolOpen, setCrearRolOpen] = useState(false);
  const [nuevoRolNombre, setNuevoRolNombre] = useState("");
  const [creandoRol, setCreandoRol] = useState(false);

  const [success, setSuccess] = useState({ show: false, title: "", subtitle: "" });
  const [idCreado, setIdCreado] = useState(null);

  const cargarRoles = () => {
    getRoles()
      .then((res) => setRoles(res.data?.items ?? res.data ?? []))
      .catch(() => toast.error("No se pudieron cargar los roles"));
  };

  useEffect(() => { cargarRoles(); }, []);

  const handleCrearRol = async () => {
    const nombre = nuevoRolNombre.trim();
    if (!nombre) return;
    setCreandoRol(true);
    try {
      const res = await crearRol({ nombre });
      const nuevo = res.data;
      const idRol = nuevo.id_rol ?? nuevo.id;
      setRoles((prev) => [...prev, nuevo]);
      form.setFieldValue("id_rol", idRol);
      toast.success("Rol creado");
      setCrearRolOpen(false);
      setNuevoRolNombre("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el rol");
    } finally {
      setCreandoRol(false);
    }
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload = {
      id_rol: values.id_rol,
      nombre: values.nombre?.trim(),
      correo: values.correo?.trim(),
      password: values.password,
    };
    setSaving(true);
    try {
      const res = await crearUsuario(payload);
      setIdCreado(res.data?.id_user ?? res.data?.id);
      setSuccess({
        show: true,
        title: "¡Usuario creado!",
        subtitle: `"${payload.nombre}" ya puede iniciar sesión con su correo y contraseña.`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="cc-page-header-card">
          <Space direction="vertical" size={2}>
            <Title level={2} className="eventos-title" style={{ marginBottom: 0 }}>Nuevo usuario</Title>
            <Text className="eventos-subtitle">Crea la cuenta, después asigna sus permisos por módulo en el detalle.</Text>
          </Space>
          <Space>
            <Button className="eventos-btn-clean" onClick={() => navigate("/app/usuarios")} disabled={saving || success.show}>Cancelar</Button>
            <Button
              type="primary"
              loading={saving}
              disabled={success.show}
              icon={<i className="fa-solid fa-plus" />}
              onClick={handleSave}
              style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
            >
              Crear usuario
            </Button>
          </Space>
        </section>

        <Form form={form} layout="vertical">
          <div className="cc-section-card">
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-user"></i></span>
              Datos de la cuenta
            </div>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="nombre" label={<span className="paq-field-label">Nombre completo</span>} rules={[{ required: true, message: "Requerido" }]}>
                  <Input placeholder="Ej. María López" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="id_rol" label={<span className="paq-field-label">Rol</span>} rules={[{ required: true, message: "Requerido" }]}>
                  <Select
                    placeholder="Selecciona un rol"
                    showSearch
                    optionFilterProp="label"
                    options={roles.map((r) => ({ value: r.id_rol ?? r.id, label: r.nombre }))}
                  />
                </Form.Item>
                <div className="cc-select-helper">
                  ¿No encuentras el rol que buscas?{" "}
                  <a className="cc-select-helper-link" onClick={() => setCrearRolOpen(true)}>Crea uno nuevo</a>
                </div>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="correo"
                  label={<span className="paq-field-label">Correo (para iniciar sesión)</span>}
                  rules={[{ required: true, message: "Requerido" }, { type: "email", message: "Correo inválido" }]}
                >
                  <Input placeholder="correo@visum.com" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="password"
                  label={<span className="paq-field-label">Contraseña</span>}
                  rules={[{ required: true, message: "Requerido" }, { min: 6, message: "Mínimo 6 caracteres" }]}
                >
                  <Input.Password placeholder="Mínimo 6 caracteres" autoComplete="new-password" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>

        <SuccessOverlay
          show={success.show}
          title={success.title}
          subtitle={success.subtitle}
          onDone={() => navigate(idCreado ? `/app/usuarios/${idCreado}` : "/app/usuarios")}
        />
      </div>

      <Modal
        open={crearRolOpen}
        onCancel={() => setCrearRolOpen(false)}
        onOk={handleCrearRol}
        okText="Crear rol"
        cancelText="Cancelar"
        title={<span><i className="fa-solid fa-user-shield" style={{ marginRight: 8, color: "var(--brand-primary)" }} /> Nuevo rol</span>}
        confirmLoading={creandoRol}
        centered
        okButtonProps={{ style: { background: "var(--brand-primary)", borderColor: "var(--brand-primary)" } }}
        className="cc-quick-create-modal"
      >
        <Input
          value={nuevoRolNombre}
          onChange={(e) => setNuevoRolNombre(e.target.value)}
          placeholder="Nombre del rol..."
          autoFocus
          onPressEnter={handleCrearRol}
        />
      </Modal>
    </main>
  );
}
